import classNames from 'class-names';
import { useField } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Form as BForm, Col, Row } from 'react-bootstrap';

import { Button } from '../../Atoms/Button';
import Icon from '../../Atoms/Icon/Icon';
import { formatBytes, pluralize } from '../../lib/utils/helpers';

import styles from './file-uploader.module.css';
import { allowedFileTypes, requiredFileErrorMessage } from './helper';

export const FileUploader = ({
  children,
  onFileSelect,
  allowedNumberOfFiles = 1,
  maxFileSize = 500 * 1024 * 1024,
  allowedFileExtension = ['csv'],
  ...props
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [dragError, setDragError] = useState(false);
  const [dragErrorMessage, setDragErrorMessage] = useState('Error');

  const [selectedFiles, setSelectedFiles] = useState(null);
  const inputRef = useRef(null);

  const [field, { error }] = useField(props);
  const fieldName = field.name || 'uploadFile';
  const [isInvalid, setIsInvalid] = useState(!!error);

  useEffect(() => {
    setIsInvalid(!!error);
  }, [error]);

  useEffect(() => {
    if (dragError) {
      setTimeout(() => {
        setDragError(!dragError);
        setIsInvalid(false);
      }, 2000);
    }
  }, [dragError]);

  const handleSelectedFile = (files) => {
    setSelectedFiles(Array.from(files));
    onFileSelect(files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsInvalid(false);
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = [...e.dataTransfer.files];

    if (allowedNumberOfFiles && files.length > allowedNumberOfFiles) {
      setIsInvalid(true);
      setDragError(true);
      setDragErrorMessage(
        `Maximum ${allowedNumberOfFiles} ${pluralize(
          allowedNumberOfFiles,
          'file',
        )} can be uploaded at a time.`,
      );
      return;
    }

    if (!allowedFileTypes(allowedFileExtension).includes(files[0].type)) {
      setIsInvalid(true);
      setDragError(true);
      setDragErrorMessage(requiredFileErrorMessage(allowedFileExtension));
      return;
    }

    if (files && files.length) {
      handleSelectedFile(files);
    }
  };

  return (
    <div
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <div
        style={{ position: 'relative' }}
        className={classNames({
          'bg-gray-200': dragActive,
          'bg-a-5-red': isInvalid,
        })}
      >
        <Row
          className={classNames(
            styles.fileContainer,
            'm-0 py-3',
            isInvalid ? 'border-danger' : 'border-gray-500',
            { [styles.faded]: dragError },
          )}
        >
          <Col>
            <Row>
              <Col className="align-items-center d-flex">
                <label className="label mb-0" htmlFor={fieldName}>
                  Drag {allowedFileExtension.join().toUpperCase()}{' '}
                  {pluralize(allowedNumberOfFiles, 'File')} Here Or
                  <input
                    hidden
                    name={fieldName}
                    id={fieldName}
                    type="file"
                    accept={allowedFileTypes(allowedFileExtension)}
                    ref={inputRef}
                    onChange={(e) => handleSelectedFile(e.target.files)}
                    multiple={allowedNumberOfFiles > 1}
                  />
                </label>
                <Button
                  variant="link"
                  onClick={() => inputRef.current.click()}
                  className="py-0 pl-1"
                >
                  Browse {pluralize(allowedNumberOfFiles, 'File')}
                </Button>
              </Col>
            </Row>
            <div className="mt-n1">
              <em className="text-gray-500">
                <small>Max file size: {formatBytes(maxFileSize)}</small>
              </em>
              {isInvalid && (
                <BForm.Control.Feedback type="invalid" className="d-block">
                  {error}
                </BForm.Control.Feedback>
              )}
            </div>
            {selectedFiles && (
              <div className="py-1 text-sm">
                {selectedFiles.map((file) => (
                  <div key={file.name}>{file.name}</div>
                ))}
              </div>
            )}
          </Col>
          {children}
        </Row>
        {dragError && (
          <div
            className={classNames(
              styles.fileContainer,
              styles.dragFileElement,
              'm-0 py-3',
              'd-flex align-items-center justify-content-center',
              'border-danger text-danger',
            )}
          >
            <Icon name="danger-filled" className="h4 mb-0 mr-1" />
            {dragErrorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

FileUploader.propTypes = {
  children: PropTypes.node,
  onFileSelect: PropTypes.func,
  allowedNumberOfFiles: PropTypes.number,
  maxFileSize: PropTypes.number,
  allowedFileExtension: PropTypes.arrayOf(PropTypes.string),
};

export default FileUploader;
